/**
 * The route table (extracted so it is unit-testable). URL paths are ENGLISH code
 * (AX-9); German lives only in nav link TEXT.
 *
 * ROOT DEFECT FIX: a full-path parent record wins over its "" child (documented
 * router precedence), so a bare "/" with only a "" child renders a SILENT EMPTY
 * OUTLET. The fix (web-kit demo pattern): a dedicated redirect record for "/",
 * declared BEFORE the children-bearing "/", plus NO "" child.
 */

import type { RouteRecord, Urn } from "@platform/web-kit"
import type { ShellSession } from "./domain"
import { SETTINGS_GATE_ROLE } from "./domain"

const opportunityUrn = (id: string): Urn => `urn:career:opportunity:${id}`
const applicationUrn = (id: string): Urn => `urn:career:application:${id}`

/**
 * D3 (ratified): the generic `self_knowledge` workshop stepper is retired — its
 * slug REDIRECTS to the W1 Werte-track (a route-level redirect, never a dead page).
 * The single source of truth the route table AND the walkthrough inventory both read
 * (so the storyboard can never claim a live surface where a redirect stands).
 * `search_profile` / `profiler` keep the legacy stepper (with a »Veraltet« chip)
 * until their own redesigns.
 */
export const WORKSHOP_SLUG_REDIRECTS: ReadonlyArray<{ readonly from: string; readonly to: string }> = [
  { from: "/workshops/self-knowledge", to: "/workshops/values" },
]

/**
 * The legacy onboarding flow is COLLAPSED into the CV-Werkbank (`/compass/cv`): providing
 * material, reviewing extracted claims, and accepting the drafted CV all now happen AT the
 * CV (operator direction 2026-07-28), so a person never hops across three surfaces. The old
 * routes REDIRECT there (a route-level redirect, never a dead page) — the same D3 pattern as
 * the retired self_knowledge stepper. Single source of truth; the walkthrough inventory reads
 * it so the storyboard treats these as WEITERLEITUNG, not orphaned surfaces.
 */
export const ONBOARDING_REDIRECTS: ReadonlyArray<{ readonly from: string; readonly to: string }> = [
  { from: "/onboarding", to: "/compass/cv" },
  { from: "/onboarding/review", to: "/compass/cv" },
  { from: "/onboarding/cv", to: "/compass/cv" },
]

/**
 * S567 (ratified 2026-07-29): the Kompass IS the personal document. The dashboard-card hub
 * that used to sit at `/compass` is DELETED and the document that was one click deeper
 * (`/compass/profile`) is promoted onto `/compass` itself. The »Profil« label is retired —
 * there is no second name for the same thing any more. The old deep link REDIRECTS (the same
 * D3 pattern as the retired stepper), so every bookmark, every `next.toProfil` forward and
 * every breadcrumb that still says `/compass/profile` lands on the document rather than a
 * dead page.
 */
export const COMPASS_REDIRECTS: ReadonlyArray<{ readonly from: string; readonly to: string }> = [
  { from: "/compass/profile", to: "/compass" },
]

/** Every route-level redirect the app declares (workshop stepper + onboarding + Kompass promotion). */
export const ROUTE_REDIRECTS: ReadonlyArray<{ readonly from: string; readonly to: string }> = [
  ...WORKSHOP_SLUG_REDIRECTS,
  ...ONBOARDING_REDIRECTS,
  ...COMPASS_REDIRECTS,
]

export function buildRoutes(session: ShellSession): RouteRecord[] {
  const isAuthed = (): boolean => session.user !== null || !session.authEnforced

  const devRoutes: RouteRecord[] = [
    { pattern: "dev/heartbeat", surface: "career-heartbeat" },
    { pattern: "dev/grading", surface: "career-bench-grading" },
    // Bench-lab: one surface for index + detail. The index route sets no urn (index
    // mode); the detail route encodes the results-dir label into a urn the surface
    // reads back — a urn change REBINDS the VM (KitElement §4), so index→detail and
    // between runs both re-fetch cleanly.
    { pattern: "dev/bench-lab", surface: "career-bench-lab" },
    { pattern: "dev/bench-lab/:label", surface: "career-bench-lab", urn: ({ label }): Urn => `urn:career:bench:${label ?? ""}` },
    // Design/theme token tuning — a pure client-side operator tool (no server), so it
    // rides the devMode gate like the other diagnostics rather than the server RBAC gate.
    { pattern: "settings/theme", surface: "career-theme-settings" },
  ]

  const shellChildren: RouteRecord[] = [
    // The onboarding flow is collapsed into the CV-Werkbank (`/compass/cv`): these legacy
    // paths REDIRECT there (route-level, never a dead page). Declared BEFORE the shell's other
    // children; the guard replaces the navigation before the nominal surface ever mounts.
    ...ONBOARDING_REDIRECTS.map(
      ({ from, to }): RouteRecord => ({ pattern: from.replace(/^\//, ""), surface: "career-cv-workbench", guard: async () => ({ kind: "redirect", url: to }) }),
    ),
    { pattern: "workshops", surface: "career-workshop-list" },
    // Werte-Kartenspiel (W1 redesign V2) — a LITERAL segment declared BEFORE the
    // `:slug` param route so it wins (else `:slug` would capture "values" → the
    // runner). Ungated (operator 2026-07-23): available to every authenticated
    // user; the api procedures are auth-scoped and the game is AI-free.
    // The Werte-Interview (W1 redesign V3/V4) — planner + qualitative interview that
    // continues after the pyramid. Declared BEFORE `workshops/values` (deeper literal
    // wins) and the `:slug` param route.
    // The Werte-Sektion (W1 redesign §5b–§8, V5) — the culminating statements pendant.
    // Deepest literal, declared BEFORE the interview + cardgame routes so it wins.
    { pattern: "workshops/values/sektion", surface: "career-werte-sektion", urn: (): Urn => `urn:career:workshop:values` },
    { pattern: "workshops/values/interview", surface: "career-values-interview", urn: (): Urn => `urn:career:workshop:values` },
    { pattern: "workshops/values", surface: "career-values-cardgame", urn: (): Urn => `urn:career:workshop:values` },
    // D3: the retired self_knowledge stepper REDIRECTS to the Werte-track. Declared
    // BEFORE `:slug` so the literal wins; the guard replaces the navigation (no dead
    // page). Surface is nominal — the guard redirects before it ever mounts.
    ...WORKSHOP_SLUG_REDIRECTS.map(
      ({ from, to }): RouteRecord => ({ pattern: from.replace(/^\//, ""), surface: "career-workshop-runner", guard: async () => ({ kind: "redirect", url: to }) }),
    ),
    // W2 »Was ich suche« + W3 »Wonach ich entscheide« (W4 redesign) — purpose-built
    // surfaces, NOT the recognition-card runner. LITERAL segments declared BEFORE the
    // `:slug` param route so they win, exactly as `workshops/values` does for W1.
    { pattern: "workshops/search-profile", surface: "career-search-profile", urn: (): Urn => `urn:career:workshop:search-profile` },
    { pattern: "workshops/profiler", surface: "career-decision-profile", urn: (): Urn => `urn:career:workshop:profiler` },
    // W4 »Schreibstil« — a bespoke three-round track on its own surface, NOT the retired
    // generic stepper. LITERAL segment declared BEFORE `:slug` so it wins, exactly as the
    // W1/W2/W3 surfaces above do.
    { pattern: "workshops/writing", surface: "career-writing-style", urn: (): Urn => `urn:career:workshop:writing` },
    // The runner rebinds on slug change (KitElement urn rebind). With all three workshops
    // now on purpose-built surfaces or redirects, no live slug reaches it — it is kept
    // (not deleted) as the fallback for an unknown workshop slug.
    { pattern: "workshops/:slug", surface: "career-workshop-runner", urn: ({ slug }): Urn => `urn:career:workshop:${slug ?? ""}` },
    // S567: `/compass` mounts the DOCUMENT itself (`career-profile`), not a hub of links.
    { pattern: "compass", surface: "career-profile" },
    // »Meine Werte« — the read-only self-view (committed pyramid + confirmed statements).
    { pattern: "compass/werte", surface: "career-werte-view" },
    // The promoted document's old address REDIRECTS onto it (never a dead page). Surface is
    // nominal — the guard replaces the navigation before it ever mounts.
    ...COMPASS_REDIRECTS.map(
      ({ from, to }): RouteRecord => ({ pattern: from.replace(/^\//, ""), surface: "career-profile", guard: async () => ({ kind: "redirect", url: to }) }),
    ),
    { pattern: "compass/cv", surface: "career-cv-workbench" },
    { pattern: "compass/evidence", surface: "career-evidence-ledger" },
    { pattern: "compass/assets", surface: "career-assets" },
    { pattern: "search", surface: "career-search" },
    { pattern: "search/opportunities/:id", surface: "career-opportunity-detail", urn: ({ id }): Urn => opportunityUrn(id ?? "") },
    { pattern: "applications", surface: "career-applications" },
    { pattern: "applications/:id", surface: "career-application-review", urn: ({ id }): Urn => applicationUrn(id ?? "") },
    { pattern: "runs", surface: "career-runs" },
    { pattern: "credits", surface: "career-credits" },
    { pattern: "settings/ai", surface: "career-llm-settings" },
    // Prozess-Landkarten (Admin-Konsole) — the authored per-use-case AI process graphs.
    // Registered UNCONDITIONALLY, NOT behind `devRoutes`: `GET /console/processes` 403s anyone
    // who may not see provider cost (`assertCanSeeConsole`), and THAT server gate is the
    // boundary. Hiding the route as well would only make an entitlement look like a missing
    // feature — the same reason `/settings/ai` is routed for everyone and merely marked gated.
    // The bare path renders the FIRST authored map (never a dead page); the slug route binds a
    // urn so a slug change REBINDS the VM (KitElement §4), exactly as `workshops/:slug` does.
    { pattern: "console/processes", surface: "career-processes" },
    { pattern: "console/processes/:slug", surface: "career-processes", urn: ({ slug }): Urn => `urn:career:process:${slug ?? ""}` },
    ...(session.devMode ? devRoutes : []),
    { pattern: "*", surface: "career-not-found" }, // in-shell fallback, LAST
  ]

  return [
    { pattern: "/login", surface: "career-login" },
    // Print view — a TOP-LEVEL route (outside the shell) so no nav chrome renders and
    // @page print CSS owns the page. Reads ?version= from the query string.
    { pattern: "/cv-print", surface: "career-cv-print" },
    // Bare "/" → /search. A dedicated leaf record BEFORE the children-bearing "/"
    // (which would otherwise win as a full-path parent and mount an empty outlet).
    { pattern: "/", surface: "career-app", guard: async () => ({ kind: "redirect", url: "/search" }) },
    {
      pattern: "/",
      surface: "career-app",
      guard: async () => (isAuthed() ? { kind: "allow" } : { kind: "redirect", url: "/login" }),
      children: shellChildren,
    },
  ]
}

/** The role that ungates LLM settings — re-exported for the shell nav. */
export { SETTINGS_GATE_ROLE }
